import {useQueryClient} from '@github-ui/react-query'
import {useAnalytics} from '@github-ui/use-analytics'
import {ActionList, type ButtonProps} from '@primer/react'
import {useState} from 'react'
import {useUpdatePullRequestBranchMutation} from '../../../hooks/mutations/use-update-pull-request-branch-mutation'
import {useMergeBoxPageDataQueryKey} from '../../../page-data/loaders/use-merge-box-page-data'
import type {ViewerUpdateMethods} from '../../../types'
import {ButtonWithDropdown} from '../common/ButtonWithDropdown'

type AnalyticsEventConfig = {
  eventType: string
  target: string
  payload?: {[key: string]: unknown}
}

export type UpdateBranchAnalyticsOverrides = {
  selectMergeMethod?: AnalyticsEventConfig
  selectRebaseMethod?: AnalyticsEventConfig
  updateBranch?: AnalyticsEventConfig
}

const DEFAULT_ANALYTICS_EVENTS: Record<keyof UpdateBranchAnalyticsOverrides, AnalyticsEventConfig> = {
  selectMergeMethod: {
    eventType: 'conflicts_section.select_merge_commit_method',
    target: 'MERGEBOX_CONFLICTS_SECTION_MERGE_METHOD_MENU_ITEM',
  },
  selectRebaseMethod: {
    eventType: 'conflicts_section.select_rebase_method',
    target: 'MERGEBOX_CONFLICTS_SECTION_MERGE_METHOD_MENU_ITEM',
  },
  updateBranch: {
    eventType: 'conflicts_section.update_branch',
    target: 'MERGEBOX_CONFLICTS_SECTION_UPDATE_BRANCH_BUTTON',
  },
}

function UpdateBranchOption({
  description,
  onSelect,
  selected,
  text,
  inactiveText,
}: {
  description?: string
  onSelect: () => void
  selected: boolean
  text: string
  inactiveText?: string
}) {
  return (
    <ActionList.Item selected={selected} onSelect={onSelect} inactiveText={inactiveText}>
      {text}
      <ActionList.Description variant="block">{description}</ActionList.Description>
    </ActionList.Item>
  )
}

export function UpdateBranchButton({
  analyticsOverrides,
  headRefOid,
  onError,
  size,
  viewerUpdateMethods,
}: {
  analyticsOverrides?: UpdateBranchAnalyticsOverrides
  headRefOid: string
  onError: (message: string | null) => void
  size?: ButtonProps['size']
  viewerUpdateMethods: ViewerUpdateMethods | null
}) {
  const defaultUpdateBranchMethod =
    viewerUpdateMethods?.find(updateMethod => updateMethod.isDefault === true)?.name || 'MERGE'
  const [selectedUpdateMethod, setSelectedUpdateMethod] = useState<'MERGE' | 'REBASE'>(defaultUpdateBranchMethod)
  const [isUpdating, setIsUpdating] = useState(false)
  const {sendAnalyticsEvent} = useAnalytics()
  const mergeBoxQueryKey = useMergeBoxPageDataQueryKey()
  const queryClient = useQueryClient()

  const {mutate: updatePullRequestBranch} = useUpdatePullRequestBranchMutation({
    onSuccess: async () => {
      await queryClient.refetchQueries({queryKey: mergeBoxQueryKey}, {cancelRefetch: false})
      setIsUpdating(false)
    },
    onError: error => {
      setIsUpdating(false)
      onError(error.message)
    },
  })

  const handleUpdateBranch = () => {
    if (isUpdating) return

    setIsUpdating(true)
    onError(null)
    updatePullRequestBranch({
      updateMethod: selectedUpdateMethod,
      expectedHeadOid: headRefOid,
    })
    const event = analyticsOverrides?.updateBranch ?? DEFAULT_ANALYTICS_EVENTS.updateBranch
    sendAnalyticsEvent(event.eventType, event.target, event.payload)
  }

  const unblockedUpdateBranchMethods = viewerUpdateMethods?.filter(
    updateMethod => updateMethod.allowableStatus === 'ALLOWED' || updateMethod.allowableStatus === 'UNAVAILABLE',
  )
  const viewerCanUseMergeToUpdate = viewerUpdateMethods?.find(
    updateMethod => updateMethod.name === 'MERGE' && updateMethod.allowableStatus === 'ALLOWED',
  )
  const viewerCanUseRebaseToUpdate = viewerUpdateMethods?.find(
    updateMethod => updateMethod.name === 'REBASE' && updateMethod.allowableStatus === 'ALLOWED',
  )
  const rebaseUpdateMethodUnavailable = viewerUpdateMethods?.find(
    updateMethod => updateMethod.name === 'REBASE' && updateMethod.allowableStatus === 'UNAVAILABLE',
  )
  const rebaseUnavailableReason = viewerUpdateMethods?.find(
    updateMethod => updateMethod.name === 'REBASE',
  )?.failureReason

  const selectUpdateMethod = (updateMethod: 'MERGE' | 'REBASE') => {
    const event =
      updateMethod === 'MERGE'
        ? (analyticsOverrides?.selectMergeMethod ?? DEFAULT_ANALYTICS_EVENTS.selectMergeMethod)
        : (analyticsOverrides?.selectRebaseMethod ?? DEFAULT_ANALYTICS_EVENTS.selectRebaseMethod)
    sendAnalyticsEvent(event.eventType, event.target, event.payload)
    setSelectedUpdateMethod(updateMethod)
  }

  const updateBranchList = viewerUpdateMethods ? (
    <ActionList selectionVariant="single" showDividers>
      {viewerCanUseMergeToUpdate && (
        <UpdateBranchOption
          description="The merge commit will be associated with your account."
          selected={defaultUpdateBranchMethod === 'MERGE'}
          text="Update with merge commit"
          onSelect={() => selectUpdateMethod('MERGE')}
        />
      )}
      {viewerCanUseRebaseToUpdate && (
        <UpdateBranchOption
          description="This pull request will be rebased on top of the latest changes and then force pushed."
          selected={defaultUpdateBranchMethod === 'REBASE'}
          text="Update with rebase"
          onSelect={() => selectUpdateMethod('REBASE')}
        />
      )}
      {rebaseUpdateMethodUnavailable && (
        <UpdateBranchOption
          inactiveText={rebaseUnavailableReason || 'This branch cannot be rebased at this time.'}
          selected={defaultUpdateBranchMethod === 'REBASE'}
          text="Update with rebase"
          onSelect={() => selectUpdateMethod('REBASE')}
        />
      )}
    </ActionList>
  ) : (
    <ActionList selectionVariant="single" showDividers>
      <UpdateBranchOption
        description="The merge commit will be associated with your account."
        selected={selectedUpdateMethod === 'MERGE'}
        text="Update with merge commit"
        onSelect={() => selectUpdateMethod('MERGE')}
      />
      <UpdateBranchOption
        description="This pull request will be rebased on top of the latest changes and then force pushed."
        selected={selectedUpdateMethod === 'REBASE'}
        text="Update with rebase"
        onSelect={() => selectUpdateMethod('REBASE')}
      />
    </ActionList>
  )

  return (
    <ButtonWithDropdown
      hideSecondaryButton={unblockedUpdateBranchMethods?.length === 1}
      loading={isUpdating}
      loadingAnnouncement={selectedUpdateMethod === 'MERGE' ? 'Updating branch' : 'Rebasing branch'}
      size={size}
      secondaryButtonAriaLabel="Update branch options"
      actionList={updateBranchList}
      onPrimaryButtonClick={handleUpdateBranch}
    >
      {selectedUpdateMethod === 'MERGE' ? 'Update branch' : 'Rebase branch'}
    </ButtonWithDropdown>
  )
}
